// `data-scroll-clearance` marks the element whose bottom edge a scrolled-to
// target has to clear: the header's search/menu buttons, which stay pinned to
// the viewport while the rest of the header scrolls away. That element carries
// the gap as its own bottom padding, so its edge is the whole answer — nothing
// is added here, or the gap would be counted twice.
const getScrollMarginTop = () => {
  const clearanceEl = document.querySelector('[data-scroll-clearance]')
  return clearanceEl?.getBoundingClientRect().bottom ?? 0
}

const scrollTo = (el: HTMLElement, behavior: ScrollBehavior) => {
  // native scrollIntoView applies scroll-margin exactly once —
  // scroll-into-view-if-needed subtracts it on top of compute-scroll-into-view
  // already having done so, landing the target twice this far down
  el.style.scrollMarginTop = `${getScrollMarginTop()}px`
  el.scrollIntoView({ behavior, block: 'start' })
}

// only one hash can be waited for at a time: a new navigation or target
// replaces whatever the previous one was still waiting on
let cancelPending: (() => void) | undefined

export const cancelPendingHashScroll = () => {
  cancelPending?.()
  cancelPending = undefined
}

// scrolls the element a hash (`#id` or bare id) points at into view. When it
// isn't in the DOM yet — content that renders after the route resolved, or is
// only mounted later — it waits for the element to appear, giving up after
// `timeout` ms
export const scrollToHash = (
  hash: string,
  { behavior = 'smooth', timeout = 5000 }: { behavior?: ScrollBehavior, timeout?: number } = {},
) => {
  cancelPendingHashScroll()
  if (!import.meta.client) { return }

  let id = hash.replace(/^#/, '')
  try { id = decodeURIComponent(id) } catch { /* keep as-is */ }
  if (!id) { return }

  const existing = document.getElementById(id)
  if (existing) {
    scrollTo(existing, behavior)
    return
  }

  const observer = new MutationObserver(() => {
    const el = document.getElementById(id)
    if (!el) { return }
    cancelPendingHashScroll()
    scrollTo(el, behavior)
  })
  observer.observe(document.body, { childList: true, subtree: true })
  const timer = setTimeout(cancelPendingHashScroll, timeout)

  cancelPending = () => {
    observer.disconnect()
    clearTimeout(timer)
  }
}
